import {
  DatePickerField,
  FormDropdown,
  FormikWithSchema,
  FormField,
  InfiniteComboBoxField,
  FormCheckbox,
} from '@gokrew/ui/form';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/CustomButton';
import { useAuth } from '@/context/AuthContext';
import { Form, type FormikHelpers } from 'formik';
import { useDialog } from '@gokrew/ui/context';
import type { DialogComponentProps } from '@/types/dialog';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  AddEmployeeFormValues,
  EmployeeCreatedEvent,
  HireCandidate,
  LinkAccountPrefill,
} from '@/types/employee';
import { createEmployee, createEmployeeSalary } from '@/api/employee';
import type { PayType } from '@gokrew/models/employee';
import { weeklyHoursToMinutes } from '@gokrew/ui/utility/partTimeHours';
import { addEmployeeSchema } from '@/validation/employeeSchema';
import { useInfiniteDepartments } from '@/hooks/department/useDepartment';
import { useInfiniteDesignations } from '@/hooks/designation/useDesignation';
import { DepartmentForm } from '../department/DepartmentForm';
import { DesignationForm } from '../designation/DesignationForm';
import { useTranslation } from 'react-i18next';
import { Language_Options } from '@gokrew/ui/constants/language-options';
import { MaskPatterns } from '@gokrew/ui/constants/mask-patterns';
import { MaskedField } from '@gokrew/ui/form/MaskedField';
import { TranslationNS } from '@/constants/translation-namespaces';
import { FormikSubmitButton } from '@gokrew/ui/components/FormikSubmitButton';
import { usePermissions } from '@/context/PermissionContext';
import { permissionKeys } from '@/utility';
import { useLinkedBranches } from '@/hooks/branches/useBranches';
import { LeavePreviewSection } from './LeavePreviewSection';
import { OnboardingWorkflowField } from './OnboardingWorkflowField';
import { ManagesTeamSection } from './ManagesTeamSection';
import { ReportsToField } from './ReportsToField';
import { useApplyManageTeam } from '@/hooks/reporting/useManageTeam';
import {
  emptyManageTeamSelection,
  type ManageTeamPlan,
} from '@/utils/manageTeam';
import { useOnboardingTemplates } from '@/hooks/employees/useOnboardingTemplates';
import { useLeaveAllocation } from '@/hooks/employees/useLeaveAllocation';
import { useMemo, useRef } from 'react';
import { QueryKeys } from '@gokrew/models/constants';
import type { InviteCredentials } from '@gokrew/ui/components/InviteSuccessDialog';
import { useHireJobPrefill } from '@/hooks/employees/useHireJobPrefill';
import { JobPrefillSeed } from './JobPrefillSeed';
import { defaultEmployeeBranchId } from './hireLocation';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { linkableAccountFrom } from '@/utils/noEmail';
import styles from './AddEmployeeForm.module.css';

/**
 * A form component for creating or editing an employee family
 *
 */
export function AddEmployeeForm({
  dialogId,
  onSubmit,
  onClose,
  onInviteSuccess,
  candidate,
  defaultOnboardingTaskListId,
  onCreated,
  linkAccount,
}: DialogComponentProps & {
  onInviteSuccess?: (credentials: InviteCredentials) => void;
  /**
   * A hired applicant to prefill from, Job Title and Department from its job.
   * With `applicationId`, the server links the application on save.
   */
  candidate?: HireCandidate;
  /** Workflow to preselect; see `OnboardingWorkflowField`. */
  defaultOnboardingTaskListId?: string | null;
  /** Called once the employee is saved; the caller starts the workflow. */
  onCreated?: (created: EmployeeCreatedEvent) => void;
  /**
   * "Link to an employee" on a Not linked administrator (admin access spec
   * 2026-10-31): the employee file is created on THIS login (the existing
   * `linkUserId` path, D159), so the name and email start filled and the
   * email cannot change.
   */
  linkAccount?: LinkAccountPrefill;
}) {
  const { companyId, selectedCompany, branchId } = useAuth();
  const { data: branchesData, isError: branchesFailed } = useLinkedBranches();
  const linkedBranches = branchesData?.data;
  const { closeDialog, openDialog } = useDialog();
  const queryClient = useQueryClient();
  const { t } = useTranslation(TranslationNS.Employee);
  const { allocationState, setAllocationState, saveAllocations } =
    useLeaveAllocation();
  const { permissions } = usePermissions();
  const canCreateDepartment = permissions.has(permissionKeys.department.create);
  const canCreateDesignation = permissions.has(
    permissionKeys.designation.create
  );
  const onboardingTemplates = useOnboardingTemplates();
  // "Reports to" and "Manages a team?" need `reporting.update` (the server
  // enforces it on every move).
  const canSetReporting = permissions.has(permissionKeys.reporting.update);
  // A signed offer's salary can become the first salary record (D63).
  const offerSalary = candidate?.offer?.salary ?? null;
  const canCreateSalaryFromOffer =
    !!offerSalary && permissions.has(permissionKeys.employee_salary.create);
  const teamPlanRef = useRef<ManageTeamPlan | null>(null);
  const applyTeamPlan = useApplyManageTeam();
  const { onSeeded, designationSeeds, departmentSeeds, resolveDesignationId } =
    useHireJobPrefill(candidate?.job);

  const createMutation = useMutation({
    mutationFn: ({
      partTimeWeeklyHours,
      onboardingTaskListId: _workflow, // eslint-disable-line @typescript-eslint/no-unused-vars
      managesTeam: _managesTeam, // eslint-disable-line @typescript-eslint/no-unused-vars
      teamSelection: _team, // eslint-disable-line @typescript-eslint/no-unused-vars
      reportsTo: _reportsTo, // eslint-disable-line @typescript-eslint/no-unused-vars
      createSalaryFromOffer: _salary, // eslint-disable-line @typescript-eslint/no-unused-vars
      ...value
    }: AddEmployeeFormValues & {
      createSalaryFromOffer?: boolean;
      sendInvitationMail?: boolean;
    }) =>
      createEmployee(companyId!, {
        ...value,
        // No email (D157): no address is sent and nothing to invite to.
        user: value.user?.hasNoEmail
          ? { ...value.user, email: '' }
          : value.user,
        sendInvitationMail: value.user?.hasNoEmail
          ? false
          : value.sendInvitationMail,
        recruitingApplicationId: candidate?.applicationId,
        // Only meaningful for the type that shows the field; a date picked
        // before switching to another type would otherwise still be sent.
        contractEndDate:
          value.employmentType === 'contract'
            ? value.contractEndDate
            : undefined,
        partTimeWeeklyMinutes:
          value.employmentType === 'part_time'
            ? weeklyHoursToMinutes(partTimeWeeklyHours)
            : null,
      }),
  });
  /**
   * Closes the dialog with the given dialogId.
   * This function is used to close the dialog after a successful submission or cancellation.
   */
  const closeDialogHandler = () => {
    if (dialogId) closeDialog(dialogId);
  };

  /**
   * Cancels the form submission and closes the dialog.
   * It calls the closeDialogHandler function to close the dialog with the given dialogId,
   * and if onClose is provided, it calls the onClose function to notify the parent component
   * that the dialog has been closed.
   */
  const cancelHandler = () => {
    closeDialogHandler();
    if (onClose) onClose();
  };

  /**
   * Handles the form submission by calling the appropriate mutation (create or update)
   * and handling the success and error cases.
   *
   * @param {AddEmployeeFormValues} values - The form values to be submitted
   * @param {FormikHelpers<T>} { setSubmitting } - The Formik helpers object
   */
  const saveHandler = async <T,>(
    values: AddEmployeeFormValues & {
      sendInvitationMail?: boolean;
      createSalaryFromOffer?: boolean;
    },
    helpers: FormikHelpers<T>,
    /** An administrator's account to attach the record to (D159). */
    linkUserId: string | undefined = linkAccount?.userId
  ) => {
    const { setSubmitting, setFieldValue } = helpers;
    try {
      const designationId = await resolveDesignationId(
        values.designationId,
        values.branchId,
        setFieldValue
      );
      const employeeResp = await createMutation.mutateAsync({
        ...values,
        user: linkUserId ? { ...values.user, linkUserId } : values.user,
        designationId,
      });
      const responseData = employeeResp?.data;

      const employeeId = responseData?.employee?.id || responseData?.id;

      await saveAllocations(employeeId);

      // "Create the salary record from the offer": effective on the start
      // date. A refusal leaves the employee saved and says so.
      if (
        canCreateSalaryFromOffer &&
        values.createSalaryFromOffer &&
        employeeId &&
        offerSalary
      ) {
        try {
          await createEmployeeSalary(companyId!, employeeId, {
            amount: Number(offerSalary.amount),
            currency: offerSalary.currency,
            payType: offerSalary.payType as PayType,
            effectiveFrom: values.dateOfJoining || candidate?.offer?.startDate,
          });
        } catch {
          toast.error(t('employee.toast_message.offer_salary_failed'));
        }
      }

      // "Reports to" and "Manages a team?": applied once the employee exists.
      // A move that fails (a loop, a group that changed) leaves the employee
      // saved; the toast names who was not moved and points to their page.
      const teamPlan = teamPlanRef.current;
      if (
        canSetReporting &&
        employeeId &&
        teamPlan &&
        (teamPlan.ownLine || teamPlan.count)
      ) {
        await applyTeamPlan(
          teamPlan,
          employeeId,
          `${values.user?.firstName ?? ''} ${values.user?.lastName ?? ''}`.trim()
        );
      }

      queryClient.invalidateQueries({ queryKey: [QueryKeys.Employees] });

      if (linkUserId) {
        toast.success(
          t('link_account.linked', {
            name: `${values.user?.firstName ?? ''} ${values.user?.lastName ?? ''}`.trim(),
          })
        );
      } else if (!responseData?.isNewUser) {
        toast(
          values.sendInvitationMail
            ? t('employee.toast_message.user_exists_invited')
            : t('employee.toast_message.user_exists')
        );
      } else {
        toast.success(t('employee.toast_message.created'));

        if (
          values.sendInvitationMail &&
          responseData?.rawPassword &&
          onInviteSuccess
        ) {
          onInviteSuccess({
            email: responseData.email,
            tempPassword: responseData.rawPassword,
            loginUrl: responseData.portalUrl ?? '',
            firstName: responseData?.employee?.user?.firstName,
          });
        }
      }
      if (dialogId) closeDialog(dialogId);
      if (onSubmit) onSubmit();
      if (onCreated && employeeId) {
        const workflow = onboardingTemplates?.find(
          (template) => template.id === values.onboardingTaskListId
        );
        onCreated({
          employeeId,
          firstName: values.user?.firstName ?? '',
          lastName: values.user?.lastName ?? '',
          hasJoiningDate: !!values.dateOfJoining,
          onboardingWorkflow: workflow ?? null,
        });
      }
    } catch (e) {
      // The email belongs to an administrator of this company (D159): offer
      // to link the record to their account; nothing is ever merged.
      const linkable = linkableAccountFrom(e);
      if (linkable && !linkUserId) {
        openDialog({
          title: t('link_account.title'),
          content: (
            <ConfirmationDialog
              confirmLabel={t('link_account.confirm')}
              onSubmit={() =>
                void saveHandler(values, helpers, linkable.userId)
              }
            >
              <p>{t('link_account.description', { name: linkable.name })}</p>
            </ConfirmationDialog>
          ),
        });
        return;
      }
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  /** Initializes the form values with the provided employee famoly data. */
  const initialValues = useMemo(() => {
    const name = candidate?.fullName.trim().split(/\s+/) ?? [];
    return {
      user: {
        email: linkAccount?.email ?? candidate?.email ?? '',
        firstName: linkAccount?.firstName ?? name[0] ?? '',
        lastName: linkAccount?.lastName ?? name.slice(1).join(' '),
        // Optional: the profile's personal phone (`user.phoneNumber`).
        phoneNumber: '',
        // The invitation goes out in this language; the company's by default.
        language: selectedCompany?.language === 'fr' ? 'fr' : 'en',
        hasNoEmail: false,
      },
      sendInvitationMail: false,
      createSalaryFromOffer: canCreateSalaryFromOffer,
      managesTeam: false,
      teamSelection: emptyManageTeamSelection(),
      reportsTo: null,
      designationId: undefined,
      departmentId: undefined,
      employeeReferenceId: undefined,
      employmentType: undefined,
      contractEndDate: undefined,
      partTimeWeeklyHours: undefined,
      // The signed offer's start date (YYYY-MM-DD, the picker's format).
      dateOfJoining: candidate?.offer?.startDate,
      // The hire's job location, else the header's, else the main one.
      branchId:
        defaultEmployeeBranchId(linkedBranches ?? [], [
          candidate?.branchId,
          branchId,
        ]) ??
        (branchId || undefined),
      leaveEligibleFrom: undefined,
      isProbationEnabled: false,
      probationPeriod: 90,
      onboardingTaskListId: '',
    };
  }, [
    branchId,
    linkedBranches,
    candidate,
    selectedCompany?.language,
    canCreateSalaryFromOffer,
    linkAccount,
  ]);

  /** A mapping of button labels based on the form mode (create or edit). */
  const btnLabels = {
    create: [t('employee.button.create'), t('employee.button.creating')],
  };
  /** Determines the mode of the form (create or edit) based on the isEdit flag. */
  const mode = 'create';
  const addDepartmentDialog = () =>
    openDialog({
      title: t('employee.dialog_titles.department'),
      content: <DepartmentForm />,
    });

  const addDesignationDialog = () =>
    openDialog({
      title: t('employee.dialog_titles.designation'),
      content: <DesignationForm />,
    });

  const branchOptions = useMemo(
    () =>
      linkedBranches?.map((branch) => ({
        value: branch?.id,
        label: branch?.name,
      })) ?? [],
    [linkedBranches]
  );

  // Several locations to choose from: the server needs one (a hire from the
  // pipeline included), whatever the company's multi-branch setting says.
  const showBranchSelection = branchOptions.length > 1;

  // The Location's default needs the admin's locations: wait for them.
  if (!linkedBranches && !branchesFailed) return null;

  return (
    <FormikWithSchema
      initialValues={initialValues}
      validationSchema={addEmployeeSchema(t, showBranchSelection)}
      onSubmit={saveHandler}
    >
      {({ isSubmitting, values }) => {
        return (
          <Form>
            {candidate?.job && (
              <JobPrefillSeed
                job={candidate.job}
                canCreateDesignation={canCreateDesignation}
                requiresBranch={showBranchSelection}
                onSeeded={onSeeded}
              />
            )}
            <div className="grid grid-cols-1 xxs:grid-cols-2 gap-x-4">
              <FormField
                name="user.firstName"
                label={t('form.fields.first_name.label')}
                type="text"
                placeholder={t('form.fields.first_name.placeholder')}
              />
              <FormField
                name="user.lastName"
                label={t('form.fields.last_name.label')}
                type="text"
                placeholder={t('form.fields.last_name.placeholder')}
              />
              {!values.user?.hasNoEmail && (
                <FormField
                  name="user.email"
                  label={t('form.fields.user_email.label')}
                  type="email"
                  placeholder={t('form.fields.user_email.placeholder')}
                  infoText={
                    linkAccount
                      ? t('link_account.email_locked')
                      : t('form.fields.user_email.info')
                  }
                  disabled={!!linkAccount}
                />
              )}
              <FormDropdown
                name="user.language"
                label={t('form.fields.user_language.label')}
                placeholder={t('form.fields.user_language.placeholder')}
                infoText={t('form.fields.user_language.info')}
                options={Language_Options.map((lang) => ({
                  label: lang.label,
                  value: lang.code,
                }))}
              />
              {!linkAccount && (
                <div className="col-span-full">
                  <FormCheckbox
                    name="user.hasNoEmail"
                    label={t('form.fields.has_no_email.label')}
                  />
                  {values.user?.hasNoEmail && (
                    <p className={styles.noEmailNote}>
                      {t('form.fields.has_no_email.note')}
                    </p>
                  )}
                </div>
              )}
              {showBranchSelection && (
                <FormDropdown
                  name="branchId"
                  label={t('form.fields.branchId.label')}
                  placeholder={t('form.fields.branchId.placeholder')}
                  options={branchOptions}
                  none
                />
              )}
              <MaskedField
                name="user.phoneNumber"
                label={t('form.fields.phone_number.label')}
                type="text"
                placeholder={t('form.fields.phone_number.placeholder')}
                mask={MaskPatterns.PHONE_NUMBER}
              />
              <InfiniteComboBoxField
                name="designationId"
                label={t('form.fields.designation_id.label')}
                placeholder={t('form.fields.designation_id.placeholder')}
                useHook={useInfiniteDesignations}
                disabled={showBranchSelection && !values?.branchId}
                baseParams={{
                  limit: 20,
                  status: 'active',
                  ...(values?.branchId ? { branchId: values.branchId } : {}),
                }}
                mapOptions={(data) =>
                  data?.data?.data?.map((d) => ({
                    value: d.id,
                    label: d.name,
                  })) ?? []
                }
                seedOptions={designationSeeds}
                onAddNew={
                  canCreateDesignation ? addDesignationDialog : undefined
                }
              />
              <InfiniteComboBoxField
                name="departmentId"
                label={t('form.fields.department_id.label')}
                placeholder={t('form.fields.department_id.placeholder')}
                useHook={useInfiniteDepartments}
                disabled={showBranchSelection && !values?.branchId}
                baseParams={{
                  limit: 20,
                  status: 'active',
                  ...(values?.branchId ? { branchId: values.branchId } : {}),
                }}
                mapOptions={(data) =>
                  data?.data?.data?.map((d) => ({
                    value: d.id,
                    label: d.name,
                  })) ?? []
                }
                seedOptions={departmentSeeds}
                onAddNew={canCreateDepartment ? addDepartmentDialog : undefined}
              />
              <FormDropdown
                name="employmentType"
                label={t('form.fields.employment_type.label')}
                placeholder={t('form.fields.employment_type.placeholder')}
                options={[
                  {
                    label: t('employee.employment_type.full_time'),
                    value: 'full_time',
                  },
                  {
                    label: t('employee.employment_type.part_time'),
                    value: 'part_time',
                  },
                  {
                    label: t('employee.employment_type.contract'),
                    value: 'contract',
                  },
                  {
                    label: t('employee.employment_type.intern'),
                    value: 'intern',
                  },
                ]}
              />
              {values.employmentType === 'part_time' && (
                <FormField
                  type="number"
                  name="partTimeWeeklyHours"
                  label={t('form.fields.part_time_weekly_hours.label')}
                  placeholder={t(
                    'form.fields.part_time_weekly_hours.placeholder'
                  )}
                  min={1}
                  max={80}
                  step={0.5}
                />
              )}
              <DatePickerField
                name="dateOfJoining"
                label={t('form.fields.date_of_Joining.label')}
              />
              {values.employmentType === 'contract' && (
                <DatePickerField
                  name="contractEndDate"
                  label={t('form.fields.contract_end_date.label')}
                />
              )}

              <DatePickerField
                name="leaveEligibleFrom"
                label={t('form.fields.leave_eligible_from.label')}
                infoText={t('form.fields.leave_eligible_from.info')}
              />
              {values.isProbationEnabled && (
                <FormField
                  type="number"
                  name="probationPeriod"
                  label={t('form.fields.probation_period.label')}
                  placeholder={t('form.fields.probation_period.placeholder')}
                  min={1}
                />
              )}
              <OnboardingWorkflowField
                templates={onboardingTemplates}
                defaultTaskListId={defaultOnboardingTaskListId}
              />
              {values.user?.hasNoEmail && values.onboardingTaskListId && (
                <p className={`${styles.noEmailNote} col-span-full`}>
                  {t('form.fields.onboarding_no_email.note')}
                </p>
              )}
              {canSetReporting && <ReportsToField />}
            </div>

            <LeavePreviewSection
              values={values}
              allocationState={allocationState}
              setAllocationState={setAllocationState}
              requiresBranch={showBranchSelection}
            />

            {canSetReporting && <ManagesTeamSection planRef={teamPlanRef} />}

            <div className="flex items-center gap-2 justify-between mt-4 col-span-full">
              <div className="flex items-center justify-center gap-8">
                <FormCheckbox
                  className="col-span-full"
                  name="isProbationEnabled"
                  label={t('form.fields.is_probation_enabled.label')}
                />

                {!values.user?.hasNoEmail && (
                  <FormCheckbox
                    className="col-span-full"
                    name="sendInvitationMail"
                    label={t('form.fields.send_invitation_mail.label')}
                  />
                )}
                {canCreateSalaryFromOffer && (
                  <FormCheckbox
                    className="col-span-full"
                    name="createSalaryFromOffer"
                    label={t('form.fields.create_salary_from_offer.label')}
                  />
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={cancelHandler}
                  className="cursor-pointer"
                >
                  {t('buttons.cancel')}
                </Button>
                {/* A linked login arrives filled in: nothing has to change. */}
                <FormikSubmitButton requireDirty={!linkAccount}>
                  {isSubmitting ? btnLabels[mode][1] : btnLabels[mode][0]}
                </FormikSubmitButton>
              </div>
            </div>
          </Form>
        );
      }}
    </FormikWithSchema>
  );
}
